import {
  AdminCreateInsurancePolicy,
  AdminCreateInsurancePolicyProduct,
  AdminDeleteInsurancePolicy,
  AdminDeleteInsurancePolicyProduct,
  AdminFetchInsurancePolicies,
  AdminUpdateInsurancePolicy,
  AdminUpdateInsurancePolicyProduct,
} from 'src/constants/actionTypes'
import type { InsurancePolicy } from 'src/interfaces/insurancePolicy'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { InsurancePolicyState } from './types'

const getInitialState = (): InsurancePolicyState => {
  return {
    partnerId: null,
    loadedPartnerId: null,
    policies: [],
    loading: false,
    error: null,
    saving: false,
    deletingPermanentId: null,
    deletingProductId: null,
  }
}

export const insurancePolicyReducer = (
  state: InsurancePolicyState = getInitialState(),
  action: Action<unknown>,
): InsurancePolicyState => {
  switch (action.type) {
    case AdminFetchInsurancePolicies.type.REQUEST: {
      const payload = action.payload as { partnerId?: string } | undefined
      const partnerId = payload?.partnerId ?? null
      const isSameCustomer = partnerId === state.partnerId

      return {
        ...state,
        partnerId,
        // Only a customer SWITCH clears. A refetch of the same customer keeps the rows so the
        // tables do not blank mid-work; a switch clears them so no row of customer A is ever
        // readable while the route already says customer B.
        policies: isSameCustomer ? state.policies : [],
        loadedPartnerId: isSameCustomer ? state.loadedPartnerId : null,
        loading: true,
        error: null,
      }
    }
    case AdminFetchInsurancePolicies.type.SUCCESS: {
      const policies = (action.payload as InsurancePolicy[] | undefined) ?? []

      return {
        ...state,
        policies,
        loadedPartnerId: state.partnerId,
        loading: false,
        error: null,
      }
    }
    // A failure settles the customer too — the page stops waiting and shows its retry banner.
    // The rows are left alone: after a switch they are already empty, and after a same-customer
    // refetch failure the previous good data is better than a blank table beside the error.
    case AdminFetchInsurancePolicies.type.FAILED:
      return {
        ...state,
        loadedPartnerId: state.partnerId,
        loading: false,
        error: resolveErrorMessage(action.payload, 'Failed to load insurance policies'),
      }

    // Create, update and delete never write to `policies` — the list is refetched on success,
    // which is the single source of truth. An immutable mutation rewrites the policy's `id` and
    // every product row id, so a locally patched row could not be reconstructed correctly.
    case AdminCreateInsurancePolicy.type.REQUEST:
    case AdminUpdateInsurancePolicy.type.REQUEST:
    case AdminCreateInsurancePolicyProduct.type.REQUEST:
    case AdminUpdateInsurancePolicyProduct.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    case AdminCreateInsurancePolicy.type.SUCCESS:
    case AdminCreateInsurancePolicy.type.FAILED:
    case AdminUpdateInsurancePolicy.type.SUCCESS:
    case AdminUpdateInsurancePolicy.type.FAILED:
    case AdminCreateInsurancePolicyProduct.type.SUCCESS:
    case AdminCreateInsurancePolicyProduct.type.FAILED:
    case AdminUpdateInsurancePolicyProduct.type.SUCCESS:
    case AdminUpdateInsurancePolicyProduct.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    case AdminDeleteInsurancePolicy.type.REQUEST: {
      const payload = action.payload as { insurancePolicyPermanentId?: string } | undefined

      return {
        ...state,
        deletingPermanentId: payload?.insurancePolicyPermanentId ?? null,
      }
    }
    // Cleared on FAILED as well as SUCCESS: a 400 'Insurance policy is in use' must not leave
    // the row's Delete button spinning forever.
    case AdminDeleteInsurancePolicy.type.SUCCESS:
    case AdminDeleteInsurancePolicy.type.FAILED:
      return {
        ...state,
        deletingPermanentId: null,
      }

    case AdminDeleteInsurancePolicyProduct.type.REQUEST: {
      const payload = action.payload as { insuranceProductId?: string } | undefined

      return {
        ...state,
        deletingProductId: payload?.insuranceProductId ?? null,
      }
    }
    case AdminDeleteInsurancePolicyProduct.type.SUCCESS:
    case AdminDeleteInsurancePolicyProduct.type.FAILED:
      return {
        ...state,
        deletingProductId: null,
      }

    default:
      return state
  }
}
